import type { UsageSummary } from '@rp/contracts'

const DAY_MS = 24 * 60 * 60 * 1000
const PERCENT = 100
const FIRST_DAY = 1
const FULL = 100

export type BudgetOutlook = 'exhausted' | 'overspending' | 'on_track'

export type BudgetPace = {
  percentUsed: number
  percentElapsed: number
  daysElapsed: number
  daysInPeriod: number
  projectedTokens: number
  exhaustsOn: string | null
  outlook: BudgetOutlook
}

type PeriodBudget = Pick<UsageSummary, 'periodStart' | 'periodEnd' | 'budget'>

const startOfDay = (day: string): number => Date.parse(`${day}T00:00:00.000Z`)

const dayOf = (moment: Date): string => moment.toISOString().slice(0, 10)

const spanInDays = (from: string, to: string): number => Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS)

const dayAfter = (from: string, offset: number): string => new Date(startOfDay(from) + offset * DAY_MS).toISOString().slice(0, 10)

const clamp = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, value))

const outlookOf = (exhausted: boolean, projected: number, budget: number): BudgetOutlook => {
  if (exhausted || budget === 0) return 'exhausted'
  return projected > budget ? 'overspending' : 'on_track'
}

export const budgetPace = ({ periodStart, periodEnd, budget }: PeriodBudget, now: Date): BudgetPace => {
  const daysInPeriod = Math.max(FIRST_DAY, spanInDays(periodStart, periodEnd) + FIRST_DAY)
  const daysElapsed = clamp(spanInDays(periodStart, dayOf(now)) + FIRST_DAY, FIRST_DAY, daysInPeriod)
  const dailyRate = budget.tokensUsed / daysElapsed
  const projectedTokens = Math.round(dailyRate * daysInPeriod)
  const outlook = outlookOf(budget.exhausted, projectedTokens, budget.monthlyTokenBudget)
  const daysToExhaust = dailyRate === 0 ? daysInPeriod + FIRST_DAY : Math.ceil(budget.monthlyTokenBudget / dailyRate)

  return {
    percentUsed:
      budget.monthlyTokenBudget === 0 ? FULL : Math.min(FULL, Math.round((budget.tokensUsed / budget.monthlyTokenBudget) * PERCENT)),
    percentElapsed: Math.round((daysElapsed / daysInPeriod) * PERCENT),
    daysElapsed,
    daysInPeriod,
    projectedTokens,
    exhaustsOn: outlook === 'overspending' && daysToExhaust <= daysInPeriod ? dayAfter(periodStart, daysToExhaust - FIRST_DAY) : null,
    outlook,
  }
}
